import React from 'react';
import { message } from "antd";
import i18n from "i18next";
import ApplicationService from "../services/ApplicationService";
import IntlMessage from "../components/IntlMessage/IntlMessage";

// Constants for NCA Layer (Digital Signature)
const NCA_LAYER_URL = "wss://127.0.0.1:13579/";
const ALLOWED_STORAGES = ['PKCS12'];
const EXT_KEY_USAGE_OIDS = ['1.3.6.1.5.5.7.3.4', '1.2.398.3.3.4.1.1'];

export async function signApplication(applicationId, userIin, userFullName) {
    let webSocket;
    try {
        webSocket = await connect();
        if (!webSocket) throw new Error("WebSocket is undefined after connection attempt");
    } catch (connectError) {
        message.error(<IntlMessage id="NCAOpenError" fallback={'Ошибка подключения к ЭЦП'} />, 5);
        throw new Error("NCAOpenError");
    }

    try {
        // Generate XML data for the application
        const applicationData = await getApplicationData(applicationId);
        if (!applicationData) {
            throw new Error("Failed to get application data");
        }

        const rs = generateApplicationXML(applicationData);
        const signData = rs;
        const signXml = {
            module: "kz.gov.pki.knca.basics",
            method: "sign",
            args: {
                allowedStorages: ALLOWED_STORAGES,
                format: "xml",
                data: signData,
                signingParams: {
                    decode: "false",
                    encapsulate: "true",
                    digested: "false",
                    tsaProfile: null,
                },
                signerParams: {
                    extKeyUsageOids: EXT_KEY_USAGE_OIDS,
                    iin: userIin,
                    bin: "",
                    serialNumber: "",
                    chain: null,
                },
                locale: "ru",
            },
        };

        webSocket.send(JSON.stringify(signXml));
        
        const response = await new Promise((resolve, reject) => {
            webSocket.onmessage = ({data}) => {
                try {
                    const response = JSON.parse(data);
                    if (!response) return reject("Invalid WebSocket response");

                    if (response.status) {
                        resolve(response.body);
                    } else {
                        reject(`Error code: ${response.code}`);
                    }
                } catch (error) {
                    reject("Error parsing WebSocket response");
                }
            };
        });

        webSocket.onmessage = null;
        
        if (response?.result) {
            // Convert signed XML to base64
            const xmlBase64 = btoa(new TextEncoder().encode(response.result).reduce((data, byte) => data + String.fromCharCode(byte), ''));
            
            // Prepare data for backend
            const docDetails = i18n.t('sign.docDetails', { name: applicationData.landPlotName });
            const signDate = new Date().toISOString();
            
            // Save signature to backend
            const saveResponse = await ApplicationService.saveApplicationSignature(
                applicationId, 
                userFullName, 
                docDetails, 
                signDate, 
                xmlBase64
            );

            if (saveResponse?.status === 200) {
                message.success(<IntlMessage id="sign.signed" fallback={'Заявка успешно подписана'} />, 5);
                return true;
            } else {
                console.error("Save signature failed:", saveResponse);
                message.error(<IntlMessage id="sign.saveError" fallback={'Ошибка сохранения подписи'} />, 5);
                return false;
            }
        } else {
            throw new Error("Invalid result from WebSocket response");
        }
    } catch (error) {
        console.error("Error during signing process:", error);
        message.error(<IntlMessage id="sign.signError" fallback={'Ошибка при подписании'} />, 5);
        throw error;
    } finally {
        if (webSocket) webSocket.close();
    }
}

async function getApplicationData(applicationId) {
    try {
        const response = await ApplicationService.getApplicationById(applicationId);
        if (response?.status === 200 && response.data) {
            return response.data;
        }
        throw new Error("Failed to fetch application data");
    } catch (error) {
        console.error("Error fetching application data:", error);
        throw error;
    }
}

function generateApplicationXML(application) {
    const xml = `<?xml version="1.0" encoding="UTF-8"?>
    <application>
        <id>${application.id}</id>
        <landPlotName>${application.landPlotName}</landPlotName>
        <plannedWaterVolume>${application.plannedWaterVolume}</plannedWaterVolume>
        <startDate>${application.startDate}</startDate>
        <endDate>${application.endDate}</endDate>
        <crops>${application.crops}</crops>
        <irrigationType>${application.irrigationType}</irrigationType>
        <agriculturalType>${application.agriculturalType}</agriculturalType>
        <status>${application.status}</status>
        <createdAt>${application.createdAt}</createdAt>
    </application>`;
    return xml;
}

export function connect() {
    if (connect.webSocket && connect.webSocket.readyState < 2) {
        return Promise.resolve(connect.webSocket);
    }

    return new Promise(function (resolve, reject) {
        try {
            connect.webSocket = new WebSocket(NCA_LAYER_URL);
        } catch (error) {
            message.error(<IntlMessage id="NCAOpenError" fallback={'Ошибка подключения к ЭЦП'} />, 5);
            throw new Error("NCAOpenError");
        }

        connect.webSocket.onopen = function () {
            resolve(connect.webSocket);
        };

        connect.webSocket.onerror = function (err) {
            message.error(<IntlMessage id="NCAOpenError" fallback={'Ошибка подключения к ЭЦП'} />, 5);
            reject(err);
        };

        connect.webSocket.onclose = function (event) {
            if (!event.wasClean) {
                console.error("WebSocket connection closed unexpectedly.");
            }
        };
    });
}